import { useEffect, useState } from 'react'

import { useAgentStatus, useDraftingTool, useThinkingSince } from '../../hooks'
import Spinner from './Spinner'
import { humanizeToolName } from './ToolCallBlock'
import { agentStatusLabel } from './thinkingLabels'

const PHRASES = [
  'permutating',
  'deliberating',
  'iterating',
  'synthesizing',
  'pondering',
  'cross-referencing',
  'compiling thought',
  'humming',
  'reticulating splines',
  'consulting the cortex',
  'beep bop boop',
  'enumerating heuristics',
  'aligning subroutines',
  'parsing intent',
  'recombobulating',
  'tracing inferences',
  'matriculating',
  'churning bits',
  'auditing the audit',
  'dusting off the ledger',
  'consulting Daystrom Institute',
  'engaging multitronic matrix',
  'running tricorder analysis',
  'scanning subspace',
  'rerouting auxiliary power',
  'calibrating heisenberg compensators',
  'polarizing positronic logic',
  'running level-3 diagnostic',
  'inverting tachyon polarity',
  'consulting the science station',
  'consulting LCARS',
  'engaging warp drive',
]

const ROTATE_MS = 2400

export default function ThinkingStatus() {
  // Step the index by one each tick rather than jumping randomly. The label
  // shown is often `pool[index % pool.length]` for a small (2-entry) status
  // pool, and a random index collapses several different indices onto the
  // same slot, so the same line repeats back-to-back. Consecutive integers
  // never share a slot for any pool of length >= 2, so stepping by one
  // guarantees the line always changes. The start is still random per turn.
  const [index, setIndex] = useState(() =>
    Math.floor(Math.random() * PHRASES.length),
  )
  const since = useThinkingSince()
  // While the model is writing a tool call's arguments (e.g. drafting a
  // report-section rewrite) nothing streams as text; show the concrete
  // activity with a growing character count instead of the whimsy.
  const draftingTool = useDraftingTool()
  // daystrom's live activity signal (esp. during a subagent's silent run).
  // Maps to Star-Trek-flavored copy below; takes priority over the generic
  // shimmer but yields to the more concrete drafting status.
  const agentStatus = useAgentStatus()

  useEffect(() => {
    const timer = window.setInterval(() => {
      setIndex((current) => current + 1)
    }, ROTATE_MS)
    return () => window.clearInterval(timer)
  }, [])

  const status = draftingTool
    ? `${humanizeToolName(draftingTool.name).toLowerCase()}${draftingTool.chars > 0 ? ` (${draftingTool.chars.toLocaleString()} characters)` : ''}`
    : agentStatus
      ? agentStatusLabel(agentStatus, index)
      : PHRASES[index % PHRASES.length]

  return (
    <output
      aria-label="Daystrom is thinking"
      className="flex w-full items-baseline gap-1.5 text-[11px] italic text-blue-6/80"
    >
      <Spinner className="tabular-nums not-italic text-blue-6" />
      <span
        key={draftingTool ? 'drafting' : index}
        className="animate-bubble-in"
      >
        {status}
      </span>
      {since !== null && <Elapsed since={since} />}
    </output>
  )
}

function Elapsed({ since }: { since: number }) {
  const [now, setNow] = useState(() => Date.now())

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1000)
    return () => window.clearInterval(timer)
  }, [])

  return (
    <span className="ml-auto tabular-nums not-italic text-foreground/40">
      {formatElapsed(now - since)}
    </span>
  )
}

function formatElapsed(ms: number): string {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000))
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60
  return `${minutes}:${seconds.toString().padStart(2, '0')}`
}
